import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ObjektStrukturTreetableService, TreeNodeData } from '../../services/objekt-struktur-treetable.service';
import { ObjektStrukturService } from '../../services/objekt-struktur.service';
import { StandortService } from '../../services/standort.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddGebaeudeDialogComponent } from './add-gebaeude-dialog/add-gebaeude-dialog.component';
import { AddSektionDialogComponent } from './add-sektion-dialog/add-sektion-dialog.component';
import { AddRaumDialogComponent } from './add-raum-dialog/add-raum-dialog.component';
import { AddIndicatorDialogComponent } from './add-indicator-dialog/add-indicator-dialog.component';
import { EditGebaeudeDialogComponent } from './edit-gebaeude-dialog/edit-gebaeude-dialog.component';
import { EditSektionDialogComponent } from './edit-sektion-dialog/edit-sektion-dialog.component';
import { EditRaumDialogComponent } from './edit-raum-dialog/edit-raum-dialog.component';
import { EditIndicatorDialogComponent } from './edit-indicator-dialog/edit-indicator-dialog.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-objekt-struktur-treetable',
  templateUrl: './objekt-struktur-treetable.component.html',
  styleUrls: ['./objekt-struktur-treetable.component.scss']
})
export class ObjektStrukturTreetableComponent implements OnInit, OnDestroy {
  private _standortId: string = '';

  treeData: any[] = [];
  loading: boolean = false;
  error: string = '';
  showIndicators: boolean = false;
  showHistory: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private objektStrukturService: ObjektStrukturTreetableService,
    private objektStrukturCrudService: ObjektStrukturService,
    private standortService: StandortService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  @Input() 
  set standortId(value: string) {
    console.log('ObjektStrukturTreetableComponent: standortId changed from', this._standortId, 'to', value);
    this._standortId = value;
    if (value && value !== this._standortId) {
      this.loadObjectStructure();
    }
  }
  
  get standortId(): string {
    return this._standortId;
  }

  @Output() indicatorAdded = new EventEmitter<void>();

  ngOnInit(): void {
    console.log('ObjektStrukturTreetableComponent: ngOnInit called with standortId:', this.standortId);
    if (this.standortId) {
      this.loadObjectStructure();
    } else {
      console.warn('ObjektStrukturTreetableComponent: No standortId provided');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load object structure data from backend
   */
  loadObjectStructure(): void {
    console.log('ObjektStrukturTreetableComponent: Loading object structure for standortId:', this.standortId, 'showHistory:', this.showHistory);
    this.loading = true;
    this.error = '';

    // Always get all items (both active and inactive) - filtering is done in frontend
    this.objektStrukturService.getObjectStructure(this.standortId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('ObjektStrukturTreetableComponent: API response received:', response);
          if (response.result === 1) {
            this.treeData = this.transformToTreeFormat(response.data);
            console.log('ObjektStrukturTreetableComponent: Transformed tree data:', this.treeData);
          } else {
            this.error = response.message || 'Error loading object structure';
            console.error('ObjektStrukturTreetableComponent: API returned error:', this.error);
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('ObjektStrukturTreetableComponent: Error fetching object structure:', error);
          console.error('ObjektStrukturTreetableComponent: Error details:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            message: error.message
          });
          this.error = 'Error loading object structure';
          this.loading = false;
        }
      });
  }


  /**
   * Toggle history view
   */
  onHistoryToggle(): void {
    console.log('History toggle changed to:', this.showHistory);
    // No need to reload data - we already have all items, just toggle the display
    // The template will handle showing/hiding based on showHistory and status
  }

  /**
   * Transform backend data to tree format
   */
  private transformToTreeFormat(data: any[]): any[] {
    console.log('ObjektStrukturTreetableComponent: Transforming data:', data);
    if (!Array.isArray(data)) {
      console.warn('ObjektStrukturTreetableComponent: Data is not an array:', data);
      return [];
    }
    
    const transformed = data.map(node => this.transformNode(node));
    console.log('ObjektStrukturTreetableComponent: Transformed result:', transformed);

    // Expand all nodes by default (Gebäude, Sektionen, and Räume)
    this.expandAllNodes(transformed);
    
    return transformed;
  }

  /**
   * Transform a single node to tree format
   */
  private transformNode(node: any): any {
    const hasChildren = node.children && node.children.length > 0;
    
    return {
      data: {
        id: node.id || node.name,
        name: node.name || 'Unnamed',
        type: node.type || 'unknown',
        description: node.description || '',
        indikator_name: node.indikator_name || '',
        indikator_type: node.indikator_type || '', // This is the indikator code
        indikator_number: node.indikator_number || null,
        schwellenwert: node.schwellenwert || '',
        value: node.value || '',
        symbol: node.symbol || '', // This is the symbol from indikator
        color: node.color || '',
        notes: node.notes || '',
        standort_id: node.standort_id || '',
        einheit: node.einheit || '',
        mehr_weniger: node.mehr_weniger || '',
        qr_code: node.qr_code || '',
        status: node.status || 1 // Add status field
      },
      children: hasChildren ? node.children.map((child: any) => this.transformNode(child)) : [],
      expanded: false,
      leaf: !hasChildren
    };
  }

  /**
   * Get node icon based on type
   */
  getNodeIcon(type: string): string {
    switch (type) {
      case 'gebaeude':
        return 'business';
      case 'sektion':
        return 'folder';
      case 'room':
        return 'home';
      case 'indicator':
        return 'trending_up';
      default:
        return 'help';
    }
  }

  /**
   * Get type label for display
   */
  getTypeLabel(type: string): string {
    switch (type) {
      case 'gebaeude':
        return 'Gebäude';
      case 'sektion':
        return 'Sektion';
      case 'room':
        return 'Raum';
      case 'indicator':
        return 'Indikator';
      default:
        return type;
    }
  }

  /**
   * Get color class for indicator status
   */
  getColorClass(color: string): string {
    switch (color) {
      case 'green':
        return 'text-green-600';
      case 'orange':
        return 'text-orange-600';
      case 'red':
        return 'text-red-600';
      case 'yellow':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Get background color class for indicator status
   */
  getBackgroundColorClass(color: string): string {
    switch (color) {
      case 'green':
        return 'bg-green-100';
      case 'orange':
        return 'bg-orange-100';
      case 'red':
        return 'bg-red-100';
      case 'yellow':
        return 'bg-yellow-100';
      default:
        return 'bg-gray-100';
    }
  }

  /**
   * Toggle node expansion
   */
  toggleNode(node: any): void {
    node.expanded = !node.expanded;
  }

  /**
   * Toggle visibility of all indicator tables
   */
  toggleShowIndicators(): void {
    this.showIndicators = !this.showIndicators;
    console.log('Toggle showIndicators to:', this.showIndicators);
  }

  /**
   * Add indicator to a room
   */
  addIndicator(roomId: string, roomName: string): void {
    console.log('Adding indicator to room:', roomId, roomName);
    
    const dialogRef = this.dialog.open(AddIndicatorDialogComponent, {
      width: '450px',
      data: { 
        roomId: roomId, 
        standortId: this.standortId 
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new indicators:', result.data);
        
        const anzahl = result.data.anzahl || 1;
        const baseName = result.data.name;
        
        // Create multiple indicators based on anzahl
        this.createMultipleIndicators(result.data, anzahl, baseName);
      }
    });
  }

  /**
   * Create multiple indicators sequentially
   */
  private createMultipleIndicators(data: any, anzahl: number, baseName: string): void {
    let createdCount = 0;
    let hasError = false;

    const createNextIndicator = (index: number) => {
      if (index > anzahl) {
        // All indicators created
        if (!hasError) {
          console.log(`Successfully created ${anzahl} indicators`);
          this.loadObjectStructure();
          this.indicatorAdded.emit();
        } else {
          alert(`Fehler beim Erstellen der Indikatoren. ${createdCount} von ${anzahl} Indikatoren wurden erstellt.`);
        }
        return;
      }

      const createData = {
        room_id: data.room_id,
        indikator_id: data.indikator_id,
        name: anzahl > 1 ? `${baseName} ${index.toString().padStart(2, '0')}` : baseName,
        schwellenwert: data.schwellenwert,
        einheit: data.einheit,
        mehr_weniger: 'mehr',
        current_value: '0',
        status: 1,
        qr_code: '',
        position_x: null,
        position_y: null,
        bemerkung: '',
        created_by: null
      };

      this.objektStrukturCrudService.createIndicator(createData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log(`Indicator ${index} created successfully:`, response);
            createdCount++;
            createNextIndicator(index + 1);
          },
          error: (error) => {
            console.error(`Error creating indicator ${index}:`, error);
            hasError = true;
            createNextIndicator(index + 1);
          }
        });
    };

    createNextIndicator(1);
  }

  /**
   * Edit existing indicator
   */
  editIndicator(indicatorId: string, indicatorName: string): void {
    // Strip the 'ind_' prefix from the indicator ID to get the actual UUID
    const actualIndicatorId = indicatorId.replace('ind_', '');
    
    // First, get the current indicator data and settings
    this.objektStrukturCrudService.getRoomIndikatorById(actualIndicatorId).subscribe({
      next: (indicatorData) => {
        // Load only the required data
        this.objektStrukturCrudService.getIndikatoren().subscribe(indikatoren => {
          const dialogRef = this.dialog.open(EditIndicatorDialogComponent, {
            width: '600px',
            data: {
              id: indicatorId,
              roomId: indicatorData.room_id,
              standortId: this.standortId,
              currentData: indicatorData,
              indikatorOptions: indikatoren || []
            },
            panelClass: 'custom-dialog-container'
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result && result.event === 'update') {
              this.objektStrukturCrudService.updateRoomIndikator(actualIndicatorId, result.data)
                .subscribe({
                  next: (response) => {
                    console.log('Indicator updated:', response);
                    this.loadObjectStructure();
                  },
                  error: (error) => {
                    console.error('Error updating indicator:', error);
                  }
                });
            } else if (result && result.event === 'delete') {
              this.objektStrukturCrudService.deleteRoomIndikator(actualIndicatorId)
                .subscribe({
                  next: (response) => {
                    console.log('Indicator deleted:', response);
                    this.loadObjectStructure();
                  },
                  error: (error) => {
                    console.error('Error deleting indicator:', error);
                  }
                });
            }
          });
        });
      },
      error: (error) => {
        console.error('Error loading indicator data:', error);
      }
    });
  }

  /**
   * Add new section
   */
  addSection(gebaeudeId: string): void {
    console.log('Adding new section for building:', gebaeudeId);
    
    const dialogRef = this.dialog.open(AddSektionDialogComponent, {
      width: '450px',
      data: { 
        gebaeudeId: gebaeudeId
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new section:', result.data);
        
        this.objektStrukturCrudService.createSektion(result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Section created successfully:', response.data);
                // Refresh the object structure
                this.loadObjectStructure();
              } else {
                console.error('Failed to create section:', response.message);
                alert('Fehler beim Erstellen der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error creating section:', error);
              alert('Fehler beim Erstellen der Sektion');
            }
          });
      }
    });
  }

  /**
   * Add new room to a section
   */
  addRoom(sectionId: string, sectionName: string): void {
    console.log('Adding new room to section:', sectionId, sectionName);
    
    const dialogRef = this.dialog.open(AddRaumDialogComponent, {
      width: '450px',
      data: { 
        sektionId: sectionId
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new room:', result.data);
        
        this.objektStrukturCrudService.createRaum(result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Room created successfully:', response.data);
                // Refresh the object structure
                this.loadObjectStructure();
              } else {
                console.error('Failed to create room:', response.message);
                alert('Fehler beim Erstellen des Raums: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error creating room:', error);
              alert('Fehler beim Erstellen des Raums');
            }
          });
      }
    });
  }

  /**
   * Add new building
   */
  addGebaeude(): void {
    console.log('Adding new building for standort:', this.standortId);
    
    const dialogRef = this.dialog.open(AddGebaeudeDialogComponent, {
      width: '450px',
      data: { standortId: this.standortId },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new building:', result.data);
        
        this.objektStrukturCrudService.createGebaeude(result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Building created successfully:', response.data);
                // Refresh the object structure
                this.loadObjectStructure();
              } else {
                console.error('Failed to create building:', response.message);
                alert('Fehler beim Erstellen des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error creating building:', error);
              alert('Fehler beim Erstellen des Gebäudes');
            }
          });
      }
    });
  }

  /**
   * Edit building
   */
  editGebaeude(id: string, name: string): void {
    console.log('Editing building:', id, name);
    
    const dialogRef = this.dialog.open(EditGebaeudeDialogComponent, {
      width: '450px',
      data: { 
        id: id, 
        name: name, 
        standortId: this.standortId 
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'update') {
        console.log('Updating building:', result.data);
        
        this.objektStrukturCrudService.updateGebaeude(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Building updated successfully:', response.data);
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to update building:', response.message);
                alert('Fehler beim Aktualisieren des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error updating building:', error);
              alert('Fehler beim Aktualisieren des Gebäudes');
            }
          });
      } else if (result && result.event === 'delete') {
        console.log('Soft deleting building:', result.data.id);
        
        this.objektStrukturCrudService.softDeleteGebaeude(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log('SoftDeleteGebaeude - Response received:', response);
              if (response.success) {
                console.log('Building deleted successfully, status in response:', response.data?.status);
                
                // Check if the backend actually updated the status
                if (response.data?.status === 1) {
                  console.warn('Backend did not update status to inactive. Status is still:', response.data.status);
                  alert('Warnung: Das Gebäude wurde möglicherweise nicht als inaktiv markiert. Bitte überprüfen Sie den Status.');
                }
                
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to delete building:', response.message);
                alert('Fehler beim Löschen des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting building:', error);
              
              // Check if the error is "already inactive"
              if (error.error && error.error.message && error.error.message.includes('already inactive')) {
                console.log('Building is already inactive, refreshing structure to show updated status');
                this.loadObjectStructure(); // Refresh to show the inactive status
              } else {
                alert('Fehler beim Löschen des Gebäudes');
              }
            }
          });
      }
    });
  }

  /**
   * Edit section
   */
  editSection(id: string, name: string): void {
    console.log('Editing section:', id, name);
    
    // Find the parent building to get gebaeude_id
    let gebaeudeId = '';
    for (const node of this.treeData) {
      for (const child of node.children || []) {
        if (child.data.id === id) {
          gebaeudeId = node.data.id;
          break;
        }
      }
      if (gebaeudeId) break;
    }
    
    const dialogRef = this.dialog.open(EditSektionDialogComponent, {
      width: '450px',
      data: { 
        id: id, 
        name: name, 
        gebaeudeId: gebaeudeId 
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'update') {
        console.log('Updating section:', result.data);
        
        this.objektStrukturCrudService.updateSektion(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Section updated successfully:', response.data);
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to update section:', response.message);
                alert('Fehler beim Aktualisieren der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error updating section:', error);
              alert('Fehler beim Aktualisieren der Sektion');
            }
          });
      } else if (result && result.event === 'delete') {
        console.log('Soft deleting section:', result.data.id);
        
        this.objektStrukturCrudService.softDeleteSektion(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log('SoftDeleteSektion - Response received:', response);
              if (response.success) {
                console.log('Section deleted successfully, status in response:', response.data?.status);
                
                // Check if the backend actually updated the status
                if (response.data?.status === 1) {
                  console.warn('Backend did not update status to inactive. Status is still:', response.data.status);
                  alert('Warnung: Die Sektion wurde möglicherweise nicht als inaktiv markiert. Bitte überprüfen Sie den Status.');
                }
                
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to delete section:', response.message);
                alert('Fehler beim Löschen der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting section:', error);
              
              // Check if the error is "already inactive"
              if (error.error && error.error.message && error.error.message.includes('already inactive')) {
                console.log('Section is already inactive, refreshing structure to show updated status');
                this.loadObjectStructure(); // Refresh to show the inactive status
              } else {
                alert('Fehler beim Löschen der Sektion');
              }
            }
          });
      }
    });
  }

  /**
   * Edit room
   */
  editRoom(id: string, name: string): void {
    console.log('Editing room:', id, name);
    
    // Find the parent section to get sektion_id
    let sektionId = '';
    for (const node of this.treeData) {
      for (const child of node.children || []) {
        for (const grandchild of child.children || []) {
          if (grandchild.data.id === id) {
            sektionId = child.data.id;
            break;
          }
        }
        if (sektionId) break;
      }
      if (sektionId) break;
    }
    
    const dialogRef = this.dialog.open(EditRaumDialogComponent, {
      width: '450px',
      data: { 
        id: id, 
        name: name, 
        sektionId: sektionId 
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'update') {
        console.log('Updating room:', result.data);
        
        this.objektStrukturCrudService.updateRaum(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Room updated successfully:', response.data);
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to update room:', response.message);
                alert('Fehler beim Aktualisieren des Raums: ' + response.message);
            }
            },
            error: (error) => {
              console.error('Error updating room:', error);
              alert('Fehler beim Aktualisieren des Raums');
            }
          });
      } else if (result && result.event === 'delete') {
        console.log('Soft deleting room:', result.data.id);
        
        this.objektStrukturCrudService.softDeleteRaum(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log('SoftDeleteRaum - Response received:', response);
              if (response.success) {
                console.log('Room deleted successfully, status in response:', response.data?.status);
                
                // Check if the backend actually updated the status
                if (response.data?.status === 1) {
                  console.warn('Backend did not update status to inactive. Status is still:', response.data.status);
                  alert('Warnung: Der Raum wurde möglicherweise nicht als inaktiv markiert. Bitte überprüfen Sie den Status.');
                }
                
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to delete room:', response.message);
                alert('Fehler beim Löschen des Raums: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting room:', error);
              
              // Check if the error is "already inactive"
              if (error.error && error.error.message && error.error.message.includes('already inactive')) {
                console.log('Room is already inactive, refreshing structure to show updated status');
                this.loadObjectStructure(); // Refresh to show the inactive status
              } else {
                alert('Fehler beim Löschen des Raums');
              }
            }
          });
      }
    });
  }

  /**
   * Print QR Labels for all objects in the structure
   */
  printQRLabels(): void {
    console.log('Printing QR labels for standort:', this.standortId);
    
    if (!this.standortId) {
      this.snackBar.open('Kein Standort ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }
    
    console.log('DEBUG: About to call generateQRIndicatorLabelsPDF');
    this.standortService.generateQRIndicatorLabelsPDF(this.standortId).subscribe({
      next: (pdfBlob: Blob) => {
        // Generate and download PDF
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-labels-standort-${this.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.snackBar.open('QR Labels PDF wird heruntergeladen', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error generating PDF:', error);
        this.snackBar.open('Fehler beim Generieren der PDF', 'Schließen', { duration: 5000 });
      }
    });
  }

  /**
   * Expand all tree nodes by default (Gebäude, Sektionen, and Räume)
   * Keep indicators table collapsed
   */
  private expandAllNodes(treeData: any[]): void {
    treeData.forEach(node => {
      // Level 1: Gebäude
      node.expanded = true;
      
      if (node.children) {
        node.children.forEach(child => {
          // Level 2: Sektionen
          child.expanded = true;
          
          if (child.children) {
            child.children.forEach(grandchild => {
              // Level 3: Räume
              grandchild.expanded = false;
              
              // Level 4: Indicators - keep collapsed
              if (grandchild.children) {
                grandchild.children.forEach(indicator => {
                  indicator.expanded = false; // Keep indicators collapsed
                });
              }
            });
          }
        });
      }
    });
    
    console.log('ObjektStrukturTreetableComponent: All tree nodes expanded by default');
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  /**
   * Get symbol URL from environment mapping
   */
  getSymbolUrl(symbolId?: string | null): string | null {
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.png`;
  }

  /**
   * Get status text for room indicator
   */
  getStatusText(status: number | boolean): string {
    if (status === 1 || status === true) {
      return 'Aktiv';
    }
    return 'Inaktiv';
  }

  /**
   * Get status classes for styling
   */
  getStatusClasses(status: number | boolean): string {
    if (status === 1 || status === true) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-red-100 text-red-800';
  }
}
